import type { Component } from "svelte";
import IconBalloon from '~icons/mdi/balloon';
import IconBank from '~icons/mdi/bank-outline';
import IconBike from '~icons/mdi/bike';
import IconCalendarStar from '~icons/mdi/calendar-star';
import IconCamera from '~icons/mdi/camera';
import IconCashOff from '~icons/mdi/cash-off';
import IconCityVariant from '~icons/mdi/city-variant';
import IconCompass from '~icons/mdi/compass-outline';
import IconDiamond from '~icons/mdi/diamond-stone';
import IconFerrisWheel from '~icons/mdi/ferris-wheel';
import IconGlassCocktail from '~icons/mdi/glass-cocktail';
import IconMapMarkerPath from '~icons/mdi/map-marker-path';
import IconOfficeBuilding from '~icons/mdi/office-building';
import IconRun from '~icons/mdi/run';
import IconSilverware from '~icons/mdi/silverware-fork-knife';
import IconStar from '~icons/mdi/star';
import IconStore from '~icons/mdi/store';
import IconTelescope from '~icons/mdi/telescope';
import IconTree from '~icons/mdi/tree';
import IconWalk from '~icons/mdi/walk';

export enum CityFeatureType {
    ACTIVITY = 'ACTIVITY',
    ANNUAL_FESTIVAL = 'ANNUAL_FESTIVAL',
    ARCHITECTURAL_HIGHLIGHT = 'ARCHITECTURAL_HIGHLIGHT',
    ATTRACTION = 'ATTRACTION',
    CITY_DETAILS = 'CITY_DETAILS',
    CITY_HIGHLIGHT = 'CITY_HIGHLIGHT',
    FOOD = 'FOOD',
    FREE_THING_TO_DO = 'FREE_THING_TO_DO',
    GREEN_SPACE = 'GREEN_SPACE',
    HIDDEN_GEM = 'HIDDEN_GEM',
    ITINERARY = 'ITINERARY',
    MARKET = 'MARKET',
    NIGHTLIFE_SPOT = 'NIGHTLIFE_SPOT',
    PHOTO_SPOT = 'PHOTO_SPOT',
    SEASONAL_HIGHLIGHT = 'SEASONAL_HIGHLIGHT',
    SCENIC_BIKE_RIDE = 'SCENIC_BIKE_RIDE',
    SCENIC_STROLL = 'SCENIC_STROLL',
    SELF_GUIDED_TOUR = 'SELF_GUIDED_TOUR',
    UNESCO_SITE = 'UNESCO_SITE',
    VIEWPOINT = 'VIEWPOINT'
}

export const CITY_FEATURE_TYPE_ICON_MAP: Record<CityFeatureType, Component> = {
    [CityFeatureType.ACTIVITY]: IconRun,
    [CityFeatureType.ANNUAL_FESTIVAL]: IconBalloon,
    [CityFeatureType.ARCHITECTURAL_HIGHLIGHT]: IconOfficeBuilding,
    [CityFeatureType.ATTRACTION]: IconFerrisWheel,
    [CityFeatureType.CITY_DETAILS]: IconCityVariant,
    [CityFeatureType.CITY_HIGHLIGHT]: IconStar,
    [CityFeatureType.FOOD]: IconSilverware,
    [CityFeatureType.FREE_THING_TO_DO]: IconCashOff,
    [CityFeatureType.GREEN_SPACE]: IconTree,
    [CityFeatureType.HIDDEN_GEM]: IconDiamond,
    [CityFeatureType.ITINERARY]: IconMapMarkerPath,
    [CityFeatureType.MARKET]: IconStore,
    [CityFeatureType.NIGHTLIFE_SPOT]: IconGlassCocktail,
    [CityFeatureType.PHOTO_SPOT]: IconCamera,
    [CityFeatureType.SEASONAL_HIGHLIGHT]: IconCalendarStar,
    [CityFeatureType.SCENIC_BIKE_RIDE]: IconBike,
    [CityFeatureType.SCENIC_STROLL]: IconWalk,
    [CityFeatureType.SELF_GUIDED_TOUR]: IconCompass,
    [CityFeatureType.UNESCO_SITE]: IconBank,
    [CityFeatureType.VIEWPOINT]: IconTelescope
};

export const CITY_FEATURE_TYPE_LABELS: Record<CityFeatureType, string> = {
    [CityFeatureType.ACTIVITY]: 'Activities',
    [CityFeatureType.ANNUAL_FESTIVAL]: 'Festivals',
    [CityFeatureType.ARCHITECTURAL_HIGHLIGHT]: 'Architecture',
    [CityFeatureType.ATTRACTION]: 'Attractions',
    [CityFeatureType.CITY_DETAILS]: 'Details',
    [CityFeatureType.CITY_HIGHLIGHT]: 'Highlights',
    [CityFeatureType.FOOD]: 'Foods',
    [CityFeatureType.FREE_THING_TO_DO]: 'Free Things to Do',
    [CityFeatureType.GREEN_SPACE]: 'Nature & Parks',
    [CityFeatureType.HIDDEN_GEM]: 'Hidden Gems',
    [CityFeatureType.ITINERARY]: 'Itineraries',
    [CityFeatureType.MARKET]: 'Markets',
    [CityFeatureType.NIGHTLIFE_SPOT]: 'Nightlife',
    [CityFeatureType.PHOTO_SPOT]: 'Photo Spots',
    [CityFeatureType.SEASONAL_HIGHLIGHT]: 'Seasons',
    [CityFeatureType.SCENIC_BIKE_RIDE]: 'Cycling Routes',
    [CityFeatureType.SCENIC_STROLL]: 'Walking Routes',
    [CityFeatureType.SELF_GUIDED_TOUR]: 'Self-Guided Tours',
    [CityFeatureType.UNESCO_SITE]: 'UNESCO Sites',
    [CityFeatureType.VIEWPOINT]: 'Viewpoints'
};

export const CITY_FEATURE_TYPE_SINGLE_LABELS: Record<CityFeatureType, string> = {
    [CityFeatureType.ACTIVITY]: 'Activity',
    [CityFeatureType.ANNUAL_FESTIVAL]: 'Festival',
    [CityFeatureType.ARCHITECTURAL_HIGHLIGHT]: 'Architecture',
    [CityFeatureType.ATTRACTION]: 'Attraction',
    [CityFeatureType.CITY_DETAILS]: 'Details',
    [CityFeatureType.CITY_HIGHLIGHT]: 'Highlight',
    [CityFeatureType.FOOD]: 'Food',
    [CityFeatureType.FREE_THING_TO_DO]: 'Free Thing to Do',
    [CityFeatureType.GREEN_SPACE]: 'Park & Nature',
    [CityFeatureType.HIDDEN_GEM]: 'Hidden Gem',
    [CityFeatureType.ITINERARY]: 'Itinerary',
    [CityFeatureType.MARKET]: 'Market',
    [CityFeatureType.NIGHTLIFE_SPOT]: 'Nightlife',
    [CityFeatureType.PHOTO_SPOT]: 'Photo Spot',
    [CityFeatureType.SEASONAL_HIGHLIGHT]: 'Season',
    [CityFeatureType.SCENIC_BIKE_RIDE]: 'Cycling Route',
    [CityFeatureType.SCENIC_STROLL]: 'Walking Route',
    [CityFeatureType.SELF_GUIDED_TOUR]: 'Self-Guided Tour',
    [CityFeatureType.UNESCO_SITE]: 'UNESCO Heritage Site',
    [CityFeatureType.VIEWPOINT]: 'Viewpoint'
};

export const ALSO_FEATURED_COPY: Partial<Record<CityFeatureType, string>> = {
    [CityFeatureType.ANNUAL_FESTIVAL]: 'Festival spot',
    [CityFeatureType.ARCHITECTURAL_HIGHLIGHT]: 'Architecture to admire',
    [CityFeatureType.CITY_HIGHLIGHT]: 'Highlight of the city',
    [CityFeatureType.FREE_THING_TO_DO]: 'Free to enjoy',
    [CityFeatureType.HIDDEN_GEM]: 'Off the beaten path',
    [CityFeatureType.MARKET]: 'Browse the stalls',
    [CityFeatureType.NIGHTLIFE_SPOT]: 'Come after dark',
    [CityFeatureType.PHOTO_SPOT]: 'Bring your camera',
    [CityFeatureType.VIEWPOINT]: 'Go for the views'
};

export const CITY_FEATURE_TYPE_ROUTE_PATHS: Record<CityFeatureType, string> = {
    [CityFeatureType.ACTIVITY]: 'activities',
    [CityFeatureType.ANNUAL_FESTIVAL]: 'festivals',
    [CityFeatureType.ARCHITECTURAL_HIGHLIGHT]: 'architecture',
    [CityFeatureType.ATTRACTION]: 'attractions',
    [CityFeatureType.CITY_DETAILS]: '',
    [CityFeatureType.CITY_HIGHLIGHT]: 'highlights',
    [CityFeatureType.FOOD]: 'foods',
    [CityFeatureType.FREE_THING_TO_DO]: 'free-things-to-do',
    [CityFeatureType.GREEN_SPACE]: 'parks-and-nature',
    [CityFeatureType.HIDDEN_GEM]: 'hidden-gems',
    [CityFeatureType.ITINERARY]: 'itineraries',
    [CityFeatureType.MARKET]: 'markets-and-shopping',
    [CityFeatureType.NIGHTLIFE_SPOT]: 'nightlife',
    [CityFeatureType.PHOTO_SPOT]: 'photo-spots',
    [CityFeatureType.SEASONAL_HIGHLIGHT]: 'seasons',
    [CityFeatureType.SCENIC_BIKE_RIDE]: 'cycling-routes',
    [CityFeatureType.SCENIC_STROLL]: 'walking-routes',
    [CityFeatureType.SELF_GUIDED_TOUR]: 'self-guided-tours',
    [CityFeatureType.UNESCO_SITE]: 'unesco-heritage-sites',
    [CityFeatureType.VIEWPOINT]: 'viewpoints'
};

// Which types have their own, individual pages that can be accessed with a slug (i.e. attraction will, but not food)
export const CITY_FEATURE_PAGE_PER_ITEM_TYPES = [
    CityFeatureType.ATTRACTION,
    CityFeatureType.GREEN_SPACE,
    CityFeatureType.ITINERARY,
    CityFeatureType.SCENIC_BIKE_RIDE,
    CityFeatureType.SCENIC_STROLL,
    CityFeatureType.SELF_GUIDED_TOUR,
    CityFeatureType.UNESCO_SITE
];

export const CITY_FEATURE_TYPE_COLOURS: Record<CityFeatureType, string> = {
    [CityFeatureType.ACTIVITY]: '#FF6B35',
    [CityFeatureType.ANNUAL_FESTIVAL]: '#F72585',
    [CityFeatureType.ARCHITECTURAL_HIGHLIGHT]: '#8B7355',
    [CityFeatureType.ATTRACTION]: '#E63946',
    [CityFeatureType.CITY_DETAILS]: '#000BD6',
    [CityFeatureType.CITY_HIGHLIGHT]: '#E6C007',
    [CityFeatureType.FOOD]: '#D62828',
    [CityFeatureType.FREE_THING_TO_DO]: '#06D6A0',
    [CityFeatureType.GREEN_SPACE]: '#2D6A4F',
    [CityFeatureType.HIDDEN_GEM]: '#9D4EDD',
    [CityFeatureType.ITINERARY]: '#4A90E2',
    [CityFeatureType.MARKET]: '#F77F00',
    [CityFeatureType.NIGHTLIFE_SPOT]: '#7209B7',
    [CityFeatureType.PHOTO_SPOT]: '#FF006E',
    [CityFeatureType.SEASONAL_HIGHLIGHT]: '#FCA311',
    [CityFeatureType.SCENIC_BIKE_RIDE]: '#38B000',
    [CityFeatureType.SCENIC_STROLL]: '#80B918',
    [CityFeatureType.SELF_GUIDED_TOUR]: '#3A86FF',
    [CityFeatureType.UNESCO_SITE]: '#B8860B',
    [CityFeatureType.VIEWPOINT]: '#0096C7'
};

export const CITY_FEATURE_KEEP_EXPLORING: Record<CityFeatureType, CityFeatureType[]> = {
    [CityFeatureType.ACTIVITY]: [CityFeatureType.SELF_GUIDED_TOUR, CityFeatureType.CITY_HIGHLIGHT, CityFeatureType.ATTRACTION, CityFeatureType.HIDDEN_GEM],
    [CityFeatureType.ANNUAL_FESTIVAL]: [CityFeatureType.SEASONAL_HIGHLIGHT, CityFeatureType.NIGHTLIFE_SPOT, CityFeatureType.FOOD, CityFeatureType.MARKET],
    [CityFeatureType.ARCHITECTURAL_HIGHLIGHT]: [CityFeatureType.UNESCO_SITE, CityFeatureType.VIEWPOINT, CityFeatureType.ATTRACTION, CityFeatureType.PHOTO_SPOT],
    [CityFeatureType.ATTRACTION]: [CityFeatureType.ARCHITECTURAL_HIGHLIGHT, CityFeatureType.UNESCO_SITE, CityFeatureType.CITY_HIGHLIGHT, CityFeatureType.PHOTO_SPOT],
    [CityFeatureType.CITY_DETAILS]: [CityFeatureType.ATTRACTION, CityFeatureType.GREEN_SPACE, CityFeatureType.UNESCO_SITE],
    [CityFeatureType.CITY_HIGHLIGHT]: [CityFeatureType.ITINERARY, CityFeatureType.ATTRACTION, CityFeatureType.ACTIVITY, CityFeatureType.FOOD],
    [CityFeatureType.FOOD]: [CityFeatureType.MARKET, CityFeatureType.NIGHTLIFE_SPOT, CityFeatureType.ANNUAL_FESTIVAL, CityFeatureType.HIDDEN_GEM],
    [CityFeatureType.FREE_THING_TO_DO]: [CityFeatureType.GREEN_SPACE, CityFeatureType.HIDDEN_GEM, CityFeatureType.VIEWPOINT, CityFeatureType.SCENIC_STROLL],
    [CityFeatureType.GREEN_SPACE]: [CityFeatureType.SCENIC_STROLL, CityFeatureType.FREE_THING_TO_DO, CityFeatureType.SCENIC_BIKE_RIDE, CityFeatureType.PHOTO_SPOT],
    [CityFeatureType.HIDDEN_GEM]: [CityFeatureType.FREE_THING_TO_DO, CityFeatureType.MARKET, CityFeatureType.NIGHTLIFE_SPOT, CityFeatureType.PHOTO_SPOT],
    [CityFeatureType.ITINERARY]: [CityFeatureType.CITY_HIGHLIGHT, CityFeatureType.SELF_GUIDED_TOUR, CityFeatureType.SEASONAL_HIGHLIGHT, CityFeatureType.ACTIVITY],
    [CityFeatureType.MARKET]: [CityFeatureType.FOOD, CityFeatureType.HIDDEN_GEM, CityFeatureType.ANNUAL_FESTIVAL, CityFeatureType.NIGHTLIFE_SPOT],
    [CityFeatureType.NIGHTLIFE_SPOT]: [CityFeatureType.FOOD, CityFeatureType.ANNUAL_FESTIVAL, CityFeatureType.HIDDEN_GEM, CityFeatureType.MARKET],
    [CityFeatureType.PHOTO_SPOT]: [CityFeatureType.VIEWPOINT, CityFeatureType.ARCHITECTURAL_HIGHLIGHT, CityFeatureType.GREEN_SPACE, CityFeatureType.HIDDEN_GEM],
    [CityFeatureType.SEASONAL_HIGHLIGHT]: [CityFeatureType.ANNUAL_FESTIVAL, CityFeatureType.ITINERARY, CityFeatureType.GREEN_SPACE, CityFeatureType.FOOD],
    [CityFeatureType.SCENIC_BIKE_RIDE]: [CityFeatureType.SCENIC_STROLL, CityFeatureType.SELF_GUIDED_TOUR, CityFeatureType.GREEN_SPACE, CityFeatureType.VIEWPOINT],
    [CityFeatureType.SCENIC_STROLL]: [CityFeatureType.SELF_GUIDED_TOUR, CityFeatureType.SCENIC_BIKE_RIDE, CityFeatureType.GREEN_SPACE, CityFeatureType.ARCHITECTURAL_HIGHLIGHT],
    [CityFeatureType.SELF_GUIDED_TOUR]: [CityFeatureType.SCENIC_STROLL, CityFeatureType.SCENIC_BIKE_RIDE, CityFeatureType.ITINERARY, CityFeatureType.ACTIVITY],
    [CityFeatureType.UNESCO_SITE]: [CityFeatureType.ARCHITECTURAL_HIGHLIGHT, CityFeatureType.ATTRACTION, CityFeatureType.CITY_HIGHLIGHT, CityFeatureType.PHOTO_SPOT],
    [CityFeatureType.VIEWPOINT]: [CityFeatureType.PHOTO_SPOT, CityFeatureType.ARCHITECTURAL_HIGHLIGHT, CityFeatureType.GREEN_SPACE, CityFeatureType.SCENIC_STROLL]
};

export const ATLAS_CITY_FEATURE_FILTER_OPTIONS: { type: CityFeatureType; label: string; }[] = [
    { type: CityFeatureType.CITY_DETAILS, label: 'Cities' },
    { type: CityFeatureType.ATTRACTION, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.ATTRACTION] },
    { type: CityFeatureType.GREEN_SPACE, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.GREEN_SPACE] },
    { type: CityFeatureType.UNESCO_SITE, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.UNESCO_SITE] },
    { type: CityFeatureType.ANNUAL_FESTIVAL, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.ANNUAL_FESTIVAL] },
    { type: CityFeatureType.ARCHITECTURAL_HIGHLIGHT, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.ARCHITECTURAL_HIGHLIGHT] },
    { type: CityFeatureType.CITY_HIGHLIGHT, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.CITY_HIGHLIGHT] },
    { type: CityFeatureType.FREE_THING_TO_DO, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.FREE_THING_TO_DO] },
    { type: CityFeatureType.HIDDEN_GEM, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.HIDDEN_GEM] },
    { type: CityFeatureType.MARKET, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.MARKET] },
    { type: CityFeatureType.NIGHTLIFE_SPOT, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.NIGHTLIFE_SPOT] },
    { type: CityFeatureType.PHOTO_SPOT, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.PHOTO_SPOT] },
    { type: CityFeatureType.VIEWPOINT, label: CITY_FEATURE_TYPE_LABELS[CityFeatureType.VIEWPOINT] }
];

export const CITY_FEATURE_TYPES_WITH_STORY: CityFeatureType[] = [CityFeatureType.ATTRACTION, CityFeatureType.GREEN_SPACE, CityFeatureType.UNESCO_SITE, CityFeatureType.HIDDEN_GEM];